import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { experts } from "@/data/experts";
import Reveal from "@/components/Reveal";
import Grain from "@/components/Grain";
import SEO, { breadcrumbSchema, personSchema } from "@/components/SEO";

/**
 * ExpertProfile — unified editorial template for all experts.
 * Renders the top-tier corporate advisory layout used across the roster.
 * The "Signature — Creator of" section renders only when `expert.creator`
 * is defined in the data source (see /src/data/experts.js).
 */
export default function ExpertProfile() {
  const { slug } = useParams();
  const expert = experts.find((e) => e.slug === slug);
  if (!expert) return <Navigate to="/expert-minds" replace />;

  const idx = experts.findIndex((e) => e.slug === expert.slug);
  const chapterNum = String(idx + 1).padStart(2, "0");
  const nextExpert = experts[(idx + 1) % experts.length];
  const nextIdx = (idx + 1) % experts.length;
  const prevExpert = experts[(idx - 1 + experts.length) % experts.length];
  const prevIdx = (idx - 1 + experts.length) % experts.length;
  const firstName = expert.name.split(" ")[0];
  const lastName = expert.name.split(" ").slice(1).join(" ");

  const bioFirst = (expert.bio && expert.bio[0]) || "";
  const inPracticeSummary =
    bioFirst.split(/(?<=[.!?])\s+/)[0] || expert.excerpt || "";

  const creator = expert.creator;

  const seoDescription =
    (expert.excerpt ||
      (expert.bio && expert.bio[0]) ||
      `${expert.name} — expert advisor at Next in Luxury.`).slice(0, 300);

  return (
    <div data-testid="page-expert-profile">
      <SEO
        title={`${expert.name} — ${expert.role || "Expert Advisor"}`}
        description={seoDescription}
        path={`/expert-minds/${expert.slug}`}
        image={expert.portrait}
        type="profile"
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Expert Minds", path: "/expert-minds" },
            { name: expert.name, path: `/expert-minds/${expert.slug}` },
          ]),
          personSchema({
            name: expert.name,
            slug: expert.slug,
            role: expert.role,
            bio: (expert.bio && expert.bio.join(" ")) || expert.excerpt || "",
            image: expert.portrait,
            location: expert.location,
            disciplines: expert.disciplines || [],
            company: expert.company,
          }),
        ]}
      />
      {/* Inline SVG filter — thresholded ink conversion for muted logos */}
      <svg
        aria-hidden="true"
        focusable="false"
        style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
      >
        <filter id="nil-logo-ink" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0.33 0.33 0.33 0 0
                    0.33 0.33 0.33 0 0
                    0.33 0.33 0.33 0 0
                    0    0    0    1 0"
          />
          <feComponentTransfer>
            <feFuncR type="discrete" tableValues="0 0 0 0 0 0 0 0 0 0 0 1 1 1 1 1" />
            <feFuncG type="discrete" tableValues="0 0 0 0 0 0 0 0 0 0 0 1 1 1 1 1" />
            <feFuncB type="discrete" tableValues="0 0 0 0 0 0 0 0 0 0 0 1 1 1 1 1" />
          </feComponentTransfer>
        </filter>
      </svg>

      {/* ═══ HERO — PORTRAIT & NAME (Ivory) ═══ */}
      <section
        className="relative"
        style={{ background: "#F4EFE7", color: "#14140F" }}
        data-testid="expert-portrait-spread"
      >
        <Grain opacity={0.028} />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-16 lg:px-24 pt-40 pb-28 md:pt-48 md:pb-36">
          {/* Utility bar — back link only */}
          <div className="mb-20">
            <Link
              to="/expert-minds"
              data-testid="expert-back-link"
              className="font-eyebrow opacity-60 editorial-link"
              style={{ color: "#14140F" }}
            >
              ⟵ View All Expert Minds
            </Link>
          </div>

          {/* Portrait + profile — asymmetric side-by-side */}
          <div className="grid grid-cols-12 gap-10 md:gap-14 items-start">
            <div className="col-span-12 md:col-span-5 md:col-start-1">
              <Reveal>
                <div
                  className="p-3"
                  style={{
                    border: "1px solid rgba(20,20,15,0.2)",
                    maxWidth: "540px",
                  }}
                >
                  <div className="overflow-hidden bg-[#0A0A08] aspect-[4/5]">
                    <img
                      src={expert.portrait}
                      alt={expert.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: expert.portraitFocus || "center 25%",
                        display: "block",
                        filter:
                          "grayscale(1) contrast(1.25) brightness(0.94) sepia(0.06)",
                      }}
                    />
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="col-span-12 md:col-span-6 md:col-start-7">
              <Reveal>
                <p
                  className="font-eyebrow mb-5"
                  style={{ color: "#8C6B3F", letterSpacing: "0.32em" }}
                >
                  Advisor
                </p>
                <div
                  className="hairline mb-10"
                  style={{ width: "24px", background: "#8C6B3F" }}
                />
                <h1
                  className="title-caps leading-[0.9]"
                  style={{
                    fontSize: "clamp(2.5rem, 6vw, 5.5rem)",
                    letterSpacing: "-0.02em",
                    textWrap: "balance",
                    color: "#14140F",
                  }}
                  data-testid="expert-name"
                >
                  {firstName.toUpperCase()}
                  <br />
                  <span
                    className="font-script"
                    style={{
                      textTransform: "none",
                      letterSpacing: 0,
                      fontSize: "1.15em",
                      color: "#8C6B3F",
                      display: "inline-block",
                      transform: "translateY(0.15em)",
                    }}
                  >
                    {lastName}
                  </span>
                </h1>
              </Reveal>

              <Reveal delay={200}>
                <div
                  className="hairline mt-20 mb-12"
                  style={{
                    width: "100%",
                    background: "rgba(20,20,15,0.15)",
                  }}
                />

                <p
                  className="font-eyebrow"
                  style={{ color: "#8C6B3F", letterSpacing: "0.32em" }}
                >
                  In Practice
                </p>
                <div
                  className="hairline mt-4 mb-10"
                  style={{ width: "24px", background: "#8C6B3F" }}
                />

                <p
                  className="font-body opacity-85 text-lg leading-relaxed mb-10"
                  style={{ color: "#14140F" }}
                >
                  {inPracticeSummary}
                </p>
              </Reveal>
            </div>
          </div>

          {/* Institutional specimen strip */}
          <div
            className="mt-14 pt-10"
            style={{ borderTop: "1px solid rgba(20,20,15,0.15)" }}
          >
            <div className="grid grid-cols-12 gap-x-12 gap-y-10">
              <div className="col-span-12 md:col-span-5 md:col-start-1">
                <p
                  className="font-eyebrow opacity-50 mb-4"
                  style={{ fontSize: "0.62rem", letterSpacing: "0.32em" }}
                >
                  Discipline
                </p>
                <p
                  className="title-caps"
                  style={{
                    fontSize: "clamp(0.95rem, 1.05vw, 1.05rem)",
                    letterSpacing: "0.14em",
                    color: "#14140F",
                    lineHeight: 1.4,
                  }}
                >
                  {expert.role}
                </p>
              </div>
              <div className="col-span-12 md:col-span-6 md:col-start-7">
                <p
                  className="font-eyebrow opacity-50 mb-4"
                  style={{ fontSize: "0.62rem", letterSpacing: "0.32em" }}
                >
                  Location
                </p>
                <p
                  className="title-caps"
                  style={{
                    fontSize: "clamp(0.95rem, 1.05vw, 1.05rem)",
                    letterSpacing: "0.14em",
                    color: "#14140F",
                    lineHeight: 1.4,
                  }}
                >
                  {expert.location}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ THE BRIEF (Ivory) ═══ */}
      <section
        className="relative"
        style={{ background: "#F4EFE7" }}
        data-testid="expert-bio-editorial"
      >
        <Grain opacity={0.028} />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-16 lg:px-24 py-20 md:py-24">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-2">
              <p
                className="font-eyebrow"
                style={{ color: "#8C6B3F", letterSpacing: "0.32em" }}
              >
                The Brief
              </p>
              <div
                className="hairline mt-4"
                style={{ width: "24px", background: "#8C6B3F" }}
              />
            </div>

            <article className="col-span-12 md:col-span-7">
              {Array.isArray(expert.bio) &&
                expert.bio.map((para, i) => (
                  <p
                    key={i}
                    className="font-body mb-8"
                    style={{
                      fontSize: "clamp(1.02rem, 1.05vw, 1.15rem)",
                      lineHeight: 1.8,
                      color: "#14140F",
                      opacity: 0.88,
                      maxWidth: "60ch",
                      textAlign: "justify",
                      textJustify: "inter-word",
                      hyphens: "auto",
                      WebkitHyphens: "auto",
                      msHyphens: "auto",
                      fontFeatureSettings: '"liga" 1, "onum" 1, "kern" 1',
                    }}
                    lang="en"
                  >
                    {para}
                  </p>
                ))}
            </article>

            <aside className="col-span-12 md:col-span-3 mt-4 md:mt-0">
              <div className="md:sticky md:top-32">
                {expert.disciplines && expert.disciplines.length > 0 && (
                  <div>
                    <p
                      className="font-eyebrow mb-2"
                      style={{ color: "#8C6B3F", letterSpacing: "0.32em" }}
                    >
                      Advisory Focus
                    </p>
                    <div
                      className="hairline mb-6"
                      style={{ width: "24px", background: "#8C6B3F" }}
                    />
                    <ul>
                      {expert.disciplines.slice(0, 6).map((d) => (
                        <li
                          key={d}
                          className="py-3"
                          style={{ borderTop: "1px solid #B8B2A6" }}
                        >
                          <p
                            className="font-serif-display"
                            style={{
                              fontSize: "0.98rem",
                              lineHeight: 1.4,
                              color: "#14140F",
                              letterSpacing: 0,
                            }}
                          >
                            {d}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ═══ SELECTED ENGAGEMENTS (Ivory) ═══ */}
      {expert.houseNames && expert.houseNames.length > 0 && (
        <section
          className="relative"
          style={{ background: "#F4EFE7" }}
          data-testid="expert-engagements"
        >
          <Grain opacity={0.028} />
          <div
            className="relative max-w-[1440px] mx-auto px-6 md:px-16 lg:px-24 pt-4 pb-20 md:pb-24"
            style={{ borderTop: "1px solid rgba(20,20,15,0.12)" }}
          >
            <div className="grid grid-cols-12 gap-8 pt-16 mb-6 items-start">
              <div className="col-span-12 md:col-span-4">
                <p
                  className="font-eyebrow"
                  style={{ color: "#8C6B3F", letterSpacing: "0.32em" }}
                >
                  Trusted By
                </p>
                <div
                  className="hairline mt-4"
                  style={{ width: "24px", background: "#8C6B3F" }}
                />
              </div>
              <div className="col-span-12 md:col-span-6 md:col-start-7 md:pt-[70px]">
                <p
                  className="font-serif-italic opacity-70"
                  style={{
                    fontSize: "clamp(1rem, 1.05vw, 1.15rem)",
                    lineHeight: 1.6,
                    color: "#14140F",
                    fontVariationSettings:
                      '"SOFT" 60, "WONK" 1, "opsz" 72',
                  }}
                >
                  A partial index of maisons and institutions with whom the
                  work has taken place — disclosed by consent. All ongoing
                  engagements remain confidential.
                </p>
              </div>
            </div>

            <div className="logo-marquee-wrapper">
              {(() => {
                const BRAND_LOGOS = {
                  Boucheron:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/h2s6wqy8_Boucheron%20Logo%20The%20Custodian%20by%20Next%20in%20Luxury.jpg",
                  Guerlain:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/e743ohn4_Gerlain%20Logo%20The%20Custodian%20by%20Next%20in%20Luxury.jpg",
                  Cartier:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/sjzefv5y_Screenshot%202026-07-13%20at%209.22.58%E2%80%AFpm.png",
                  "Patek Philippe":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/gtp04nbs_Screenshot%202026-07-17%20at%202.15.18%E2%80%AFpm.png",
                  Rolex:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/w7wvmz7r_Screenshot%202026-07-17%20at%202.15.45%E2%80%AFpm.png",
                  Hublot:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/zdyne4rv_Screenshot%202026-07-17%20at%202.14.33%E2%80%AFpm.png",
                  "Richard Mille":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/h0mkrgcc_Screenshot%202026-07-13%20at%209.21.00%E2%80%AFpm.png",
                  "Jumeirah Hotels":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/rooufuo6_Screenshot%202026-07-13%20at%209.23.53%E2%80%AFpm.png",
                  "Emirates Palace":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/zw6xa9h7_Screenshot%202026-07-13%20at%209.25.12%E2%80%AFpm.png",
                  Conrad:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/8g6ooj6x_Screenshot%202026-07-17%20at%202.16.07%E2%80%AFpm.png",
                  "Galeries Lafayette":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/s9u33xh2_Screenshot%202026-07-17%20at%202.14.49%E2%80%AFpm.png",
                  "Etihad Airways":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/q2xafcq0_Screenshot%202026-07-13%20at%208.45.36%E2%80%AFpm.png",
                  "Louis Vuitton":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/tpgz5cbn_Screenshot%202026-07-20%20at%202.56.57%E2%80%AFpm.png",
                  "Royal Jet":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/kddgl6rc_Screenshot%202026-07-13%20at%208.47.14%E2%80%AFpm.png",
                  Airbnb:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/a9clunzi_Screenshot%202026-07-13%20at%208.46.02%E2%80%AFpm.png",
                  "Audemars Piguet":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/x3lphieb_Screenshot%202026-09-11%20at%2010.01.49%E2%80%AFam.png",
                  "Autograph Collection":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/be2uinhj_Screenshot%202026-09-11%20at%2010.02.15%E2%80%AFam.png",
                  "Explora Journeys":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/vd8aqw6m_Screenshot%202026-09-11%20at%2010.09.45%E2%80%AFam.png",
                  NEOM:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/o3susi0t_Screenshot%202026-09-11%20at%2010.10.08%E2%80%AFam.png",
                  "British Airways":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/i9m3ckl1_Screenshot%202026-09-11%20at%2010.10.26%E2%80%AFam.png",
                  "Park Hyatt":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/vps0mp51_Screenshot%202026-09-11%20at%2010.11.14%E2%80%AFam.png",
                  "IHG Hotels & Resorts":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/9h7gsay4_Screenshot%202026-09-11%20at%2010.11.51%E2%80%AFam.png",
                  "Revo Hospitality Group":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/76yxbiio_Screenshot%202026-09-11%20at%2010.12.18%E2%80%AFam.png",
                  "Hilton International":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/jt2gjfyl_Screenshot%202026-09-11%20at%2010.12.48%E2%80%AFam.png",
                  "Banyan Group":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/hxozoet8_Screenshot%202026-09-11%20at%2010.13.11%E2%80%AFam.png",
                  Audi:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/q3lp96dc_Screenshot%202026-09-11%20at%2010.20.58%E2%80%AFam.png",
                  "Porsche Paris":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/uqknm942_Screenshot%202026-09-11%20at%2010.21.45%E2%80%AFam.png",
                  "Ferrari Monaco":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/h7vam6di_Screenshot%202026-09-11%20at%2010.22.25%E2%80%AFam.png",
                  "La Réserve":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/gmkhxe6q_Screenshot%202026-09-11%20at%2010.31.09%E2%80%AFam.png",
                  "Société des Bains de Mer":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/0r60b1uy_Screenshot%202026-09-11%20at%2010.31.30%E2%80%AFam.png",
                  "Le Royal Monceau":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/s2odam7g_Screenshot%202026-09-11%20at%2010.32.22%E2%80%AFam.png",
                  "Four Seasons George V":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/1jdbj4p4_Screenshot%202026-09-11%20at%2010.34.49%E2%80%AFam.png",
                  "LVMH — Guerlain":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/sihdxskg_Screenshot%202026-09-11%20at%2010.35.18%E2%80%AFam.png",
                  Hyatt:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/rv6iz9ei_Screenshot%202026-09-11%20at%2011.05.59%E2%80%AFam.png",
                  Marriott:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/izoa03lr_Screenshot%202026-09-11%20at%2011.06.13%E2%80%AFam.png",
                  Intercontinental:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/9h7gsay4_Screenshot%202026-09-11%20at%2010.11.51%E2%80%AFam.png",
                  "Mandarin Oriental":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/p94g1xwb_Screenshot%202026-09-11%20at%2010.29.54%E2%80%AFam.png",
                  "Ritz Paris":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/65d23vub_Screenshot%202026-09-11%20at%2010.30.29%E2%80%AFam.png",
                  "Jimmy Choo":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/sfvxe5rb_Screenshot%202026-09-11%20at%205.24.04%E2%80%AFpm.png",
                  Valentino:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/q9w0l5s9_Screenshot%202026-09-11%20at%205.24.19%E2%80%AFpm.png",
                  "Van Cleef & Arpels":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/mogsi4lo_Screenshot%202026-09-11%20at%205.24.53%E2%80%AFpm.png",
                  Dior:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/bkoeo4cz_Screenshot%202026-09-11%20at%205.25.09%E2%80%AFpm.png",
                  "Dorchester Collection":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/4e0pwzr3_Screenshot%202026-09-11%20at%205.32.08%E2%80%AFpm.png",
                  "St. Regis":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/95dnani3_Screenshot%202026-09-11%20at%205.32.17%E2%80%AFpm.png",
                  "Royal Court of Saudi Arabia":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/mkmeaoo8_Screenshot%202026-09-11%20at%205.33.12%E2%80%AFpm.png",
                  "French Embassy in Rome":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/lzohbj9t_Screenshot%202026-09-11%20at%205.33.41%E2%80%AFpm.png",
                  "Élysée Palace":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/qv5ut5l3_Screenshot%202026-09-11%20at%205.36.54%E2%80%AFpm.png",
                  UNESCO:
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/blr6dh5e_Screenshot%202026-09-11%20at%205.42.49%E2%80%AFpm.png",
                  "Heritage Commission — Saudi Arabia":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/z3h8j1qi_Screenshot%202026-09-11%20at%205.43.05%E2%80%AFpm.png",
                  "Diriyah Gate Development Authority":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/my7h3s59_Screenshot%202026-09-11%20at%205.43.29%E2%80%AFpm.png",
                  "Ministry of Culture — Saudi Arabia":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/alezpkcy_Screenshot%202026-09-11%20at%205.44.09%E2%80%AFpm.png",
                  "ARCHI.MEDIA TRUST-ONLUS":
                    "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/ebnqu3jm_Screenshot%202026-09-11%20at%205.44.27%E2%80%AFpm.png",
                };
                const LOGO_SIZE = {
                  "Emirates Palace": { maxWidth: 120, maxHeight: 120 },
                  "Jumeirah Hotels": { maxWidth: 170, maxHeight: 80 },
                  "Patek Philippe": { maxWidth: 140, maxHeight: 110 },
                  Rolex: { maxWidth: 130, maxHeight: 115 },
                  Hublot: { maxWidth: 130, maxHeight: 110 },
                  "Galeries Lafayette": { maxWidth: 150, maxHeight: 105 },
                  "Etihad Airways": { maxWidth: 170, maxHeight: 105 },
                  "Louis Vuitton": { maxWidth: 190, maxHeight: 55 },
                  "Royal Jet": { maxWidth: 150, maxHeight: 80 },
                  Airbnb: { maxWidth: 155, maxHeight: 60 },
                };
                const LOGO_FILTER = {
                  "Jumeirah Hotels": "url(#nil-logo-ink)",
                  "Etihad Airways": "url(#nil-logo-ink)",
                  Audi: "url(#nil-logo-ink)",
                  "La Réserve": "url(#nil-logo-ink)",
                  "Le Royal Monceau": "invert(1) contrast(1.15)",
                  Hyatt: "url(#nil-logo-ink)",
                  "Ritz Paris": "url(#nil-logo-ink)",
                  "Royal Court of Saudi Arabia": "url(#nil-logo-ink)",
                  "French Embassy in Rome": "url(#nil-logo-ink)",
                  "Heritage Commission — Saudi Arabia": "url(#nil-logo-ink)",
                  "Diriyah Gate Development Authority": "url(#nil-logo-ink)",
                  "Ministry of Culture — Saudi Arabia": "url(#nil-logo-ink)",
                  "ARCHI.MEDIA TRUST-ONLUS": "url(#nil-logo-ink)",
                };
                const LOGO_CLIP = {
                  "Patek Philippe": "inset(2px 6px 2px 6px)",
                };
                const renderLogo = (h, keyPrefix) => {
                  const logo = BRAND_LOGOS[h];
                  const size = LOGO_SIZE[h] || { maxWidth: 180, maxHeight: 70 };
                  const extraFilter = LOGO_FILTER[h] || "contrast(1.12) brightness(1.03)";
                  const clipPath = LOGO_CLIP[h];
                  return (
                    <div
                      key={`${keyPrefix}-${h}`}
                      className="flex-shrink-0 flex items-center justify-center"
                      style={{
                        width: "220px",
                        height: "170px",
                        padding: "0 1.5rem",
                      }}
                    >
                      {logo ? (
                        <img
                          src={logo}
                          alt={h}
                          className="block object-contain"
                          style={{
                            maxWidth: `${size.maxWidth}px`,
                            maxHeight: `${size.maxHeight}px`,
                            width: "auto",
                            height: "auto",
                            mixBlendMode: "multiply",
                            filter: extraFilter,
                            clipPath: clipPath,
                            WebkitClipPath: clipPath,
                          }}
                        />
                      ) : (
                        <p
                          className="title-caps text-center"
                          style={{
                            fontSize: "clamp(1.05rem, 1.35vw, 1.5rem)",
                            letterSpacing: "0.02em",
                            color: "#14140F",
                            lineHeight: 1.15,
                          }}
                        >
                          {h}
                        </p>
                      )}
                    </div>
                  );
                };
                return (
                  <div className="logo-marquee-track">
                    {expert.houseNames.map((h) => renderLogo(h, "a"))}
                    {expert.houseNames.map((h) => renderLogo(h, "b"))}
                  </div>
                );
              })()}
            </div>
          </div>
        </section>
      )}

      {/* ═══ CREATOR OF … (Ink) — renders only if data.creator present ═══ */}
      {creator && (
        <section
          className="relative"
          style={{ background: "#14140F", color: "#F4EFE7" }}
          data-testid="expert-signature"
        >
          <Grain opacity={0.04} />
          <div className="relative max-w-[1440px] mx-auto px-6 md:px-16 lg:px-24 py-16 md:py-20">
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-12 md:col-span-2">
                <p
                  className="font-eyebrow"
                  style={{ color: "#8C6B3F", letterSpacing: "0.32em" }}
                >
                  Signature
                </p>
                <div
                  className="hairline mt-4"
                  style={{ width: "24px", background: "#8C6B3F" }}
                />
              </div>
              <div className="col-span-12 md:col-span-10">
                <div className="grid grid-cols-12 gap-10 md:gap-14 items-center">
                  <div className="col-span-12 md:col-span-5">
                    <Reveal>
                      <div
                        className="humanistic-image-frame overflow-hidden"
                        style={{
                          maxWidth: "380px",
                          aspectRatio: "3/4",
                          background: "#0A0A08",
                        }}
                      >
                        <img
                          src={creator.image || expert.portrait}
                          alt={`${expert.name} — ${creator.script} ${creator.caps2}`}
                          className="humanistic-image w-full h-full object-cover"
                          style={{
                            objectPosition:
                              creator.imageFocus ||
                              (creator.image ? "center center" : "center 25%"),
                          }}
                        />
                      </div>
                    </Reveal>
                  </div>

                  <div className="col-span-12 md:col-span-7">
                    <Reveal delay={180}>
                      <h2
                        className="title-caps leading-[1.05]"
                        style={{
                          fontSize: "clamp(1.6rem, 2.6vw, 2.6rem)",
                          letterSpacing: "-0.012em",
                          color: "#F4EFE7",
                          textWrap: "balance",
                        }}
                      >
                        {creator.caps1}
                        <br />
                        <span
                          className="font-script"
                          style={{
                            textTransform: "none",
                            letterSpacing: 0,
                            fontSize: "1.55em",
                            color: "#8C6B3F",
                            display: "inline-block",
                            lineHeight: 0.95,
                            transform: "translateY(0.08em)",
                          }}
                        >
                          {creator.script}
                        </span>
                        <br />
                        {creator.caps2}
                      </h2>
                      <div
                        className="hairline my-8"
                        style={{
                          width: "48px",
                          background: "#8C6B3F",
                        }}
                      />
                      <p
                        className="font-body"
                        style={{
                          fontSize: "clamp(1rem, 1.05vw, 1.12rem)",
                          lineHeight: 1.7,
                          color: "#F4EFE7",
                          opacity: 0.85,
                          maxWidth: "52ch",
                          textAlign: "justify",
                          textJustify: "inter-word",
                          hyphens: "auto",
                          WebkitHyphens: "auto",
                          fontFeatureSettings: '"liga" 1, "onum" 1, "kern" 1',
                        }}
                        lang="en"
                      >
                        {creator.body}
                      </p>
                    </Reveal>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ═══ STATEMENT section removed by request ═══ */}

      {/* ═══ THE RECORD (Ivory) ═══ */}
      {expert.credentials && expert.credentials.length > 0 && (
        <section
          className="relative"
          style={{ background: "#F4EFE7" }}
          data-testid="expert-credentials"
        >
          <Grain opacity={0.035} />
          <div className="relative max-w-[1440px] mx-auto px-6 md:px-16 lg:px-24 py-20">
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-12 md:col-span-2">
                <p
                  className="font-eyebrow"
                  style={{ color: "#8C6B3F", letterSpacing: "0.32em" }}
                >
                  The Record
                </p>
                <div
                  className="hairline mt-4"
                  style={{ width: "24px", background: "#8C6B3F" }}
                />
              </div>
              <div className="col-span-12 md:col-span-10">
                <h2
                  className="title-caps leading-[0.95] mb-14"
                  style={{
                    fontSize: "clamp(1.75rem, 3vw, 2.75rem)",
                    letterSpacing: "-0.01em",
                    color: "#14140F",
                  }}
                >
                  Credentials
                </h2>

                <ul>
                  {expert.credentials.map((c, i) => (
                    <li
                      key={i}
                      className="py-5 flex items-baseline gap-8"
                      style={{ borderTop: "1px solid #B8B2A6" }}
                    >
                      <span
                        className="font-serif-display opacity-40"
                        style={{ fontSize: "0.9rem", minWidth: "2.5rem" }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        className="font-body"
                        style={{
                          fontSize: "1.02rem",
                          lineHeight: 1.55,
                          color: "#14140F",
                        }}
                      >
                        {c}
                      </span>
                    </li>
                  ))}
                  <li style={{ borderTop: "1px solid #B8B2A6" }} />
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ═══ WORK WITH … — CTA (Ivory) ═══ */}
      <section
        className="relative"
        style={{ background: "#F4EFE7" }}
        data-testid="expert-correspondence"
      >
        <Grain opacity={0.028} />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-16 lg:px-24 py-20 md:py-28">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-2">
              <p
                className="font-eyebrow"
                style={{ color: "#8C6B3F", letterSpacing: "0.32em" }}
              >
                Engage
              </p>
              <div
                className="hairline mt-4"
                style={{ width: "24px", background: "#8C6B3F" }}
              />
            </div>
            <div className="col-span-12 md:col-span-9">
              <Reveal>
                <h2
                  className="title-caps leading-[1.0]"
                  style={{
                    fontSize: "clamp(2rem, 3.4vw, 3.25rem)",
                    letterSpacing: "-0.015em",
                    color: "#14140F",
                    textWrap: "balance",
                  }}
                  data-testid="expert-cta-heading"
                >
                  WORK WITH{" "}
                  <span
                    className="font-script"
                    style={{
                      textTransform: "none",
                      letterSpacing: 0,
                      fontSize: "1.15em",
                      color: "#8C6B3F",
                    }}
                  >
                    {expert.name}
                  </span>
                </h2>
                <div
                  className="hairline mt-10 mb-8"
                  style={{
                    width: "48px",
                    background: "#8C6B3F",
                  }}
                />
                <p
                  className="font-serif-italic"
                  style={{
                    fontSize: "clamp(1.1rem, 1.35vw, 1.4rem)",
                    lineHeight: 1.55,
                    color: "#14140F",
                    opacity: 0.82,
                    maxWidth: "56ch",
                    textWrap: "balance",
                    fontVariationSettings:
                      '"SOFT" 60, "WONK" 1, "opsz" 72',
                  }}
                >
                  For consulting, transformation programs, and executive
                  collaborations.
                </p>
                <div className="mt-12">
                  <Link
                    to="/private-inquiry"
                    data-testid="expert-correspondence-link"
                    className="inline-flex items-center gap-4 group"
                    style={{
                      padding: "1.1rem 2.2rem",
                      border: "1px solid #14140F",
                      background: "#14140F",
                      color: "#F4EFE7",
                      letterSpacing: "0.28em",
                      fontSize: "0.72rem",
                      fontFamily: "'Marcellus SC', serif",
                      textTransform: "uppercase",
                      transition:
                        "background-color 400ms ease, color 400ms ease, letter-spacing 400ms ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "#14140F";
                      e.currentTarget.style.letterSpacing = "0.34em";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#14140F";
                      e.currentTarget.style.color = "#F4EFE7";
                      e.currentTarget.style.letterSpacing = "0.28em";
                    }}
                  >
                    Start a Conversation
                    <span
                      aria-hidden="true"
                      style={{
                        transition: "transform 400ms ease",
                        display: "inline-block",
                      }}
                    >
                      ⟶
                    </span>
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CONTINUE — pagination footer (Ivory) ═══ */}
      <section
        className="max-w-[1440px] mx-auto px-6 md:px-16 lg:px-24 py-24"
        style={{ background: "#F4EFE7" }}
        data-testid="expert-pagination"
      >
        <div className="grid grid-cols-12 gap-8 items-end">
          {/* Previous — left column */}
          <div className="col-span-6 text-left">
            <p className="font-eyebrow opacity-60">
              Previous Expert Mind
            </p>
            <h2
              className="title-caps mt-4 leading-[1.0]"
              style={{
                fontSize: "clamp(1.25rem, 2vw, 1.9rem)",
                letterSpacing: "-0.005em",
                color: "#14140F",
              }}
            >
              {prevExpert.name.split(" ")[0].toUpperCase()}{" "}
              <span
                className="font-script"
                style={{
                  textTransform: "none",
                  letterSpacing: 0,
                  fontSize: "1.15em",
                  color: "#8C6B3F",
                }}
              >
                {prevExpert.name.split(" ").slice(1).join(" ")}
              </span>
            </h2>
            <Link
              to={`/expert-minds/${prevExpert.slug}`}
              className="btn-ghost mt-6 inline-flex"
              data-testid="expert-prev-link"
            >
              <span className="arrow" style={{ marginRight: "0.5em" }}>⟵</span>
              Read Previous
            </Link>
          </div>

          {/* Next — right column */}
          <div className="col-span-6 text-right">
            <p className="font-eyebrow opacity-60">
              Continue with the Next Expert Mind
            </p>
            <h2
              className="title-caps mt-4 leading-[1.0]"
              style={{
                fontSize: "clamp(1.25rem, 2vw, 1.9rem)",
                letterSpacing: "-0.005em",
                color: "#14140F",
              }}
            >
              {nextExpert.name.split(" ")[0].toUpperCase()}{" "}
              <span
                className="font-script"
                style={{
                  textTransform: "none",
                  letterSpacing: 0,
                  fontSize: "1.15em",
                  color: "#8C6B3F",
                }}
              >
                {nextExpert.name.split(" ").slice(1).join(" ")}
              </span>
            </h2>
            <Link
              to={`/expert-minds/${nextExpert.slug}`}
              className="btn-ghost mt-6 inline-flex"
              data-testid="expert-next-link"
            >
              Read Next <span className="arrow" style={{ marginLeft: "0.5em" }}>⟶</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
